export interface NavScript {
  onLoad?: () => Promise<void>,
  getNavAction?: (nextUrl: string) => 'unload' | 'intercept' | 'none',
  onBeforeUnload?: () => Promise<{ continueUnload: boolean }>,
  onInterceptStart?: (nextUrl: string, link?: HTMLAnchorElement) => Promise<void>,
  onInterceptNav?: (nextUrl: string, nextPage: HTMLIFrameElement) => Promise<{ replaceDom: boolean }>
}

let loadedScripts: NavScript[] = []

if (window.self !== window.top) {
  if (document.currentScript)
    document.removeChild(document.currentScript)
} else {
  window.addEventListener('DOMContentLoaded', async function onLoad() {
    // Check navgate
    if (!window.localStorage.getItem('tempest-noanimate') && window.location.pathname !== '/') {
      window.sessionStorage.setItem('tempest-returnto', window.location.pathname)
      window.location.replace('/')
      return
    }

    // Hook up pop event
    window.addEventListener("popstate", (event) => {
      const destPath = window.location.pathname
      const unloadHooks = loadedScripts.filter(script => script.getNavAction?.(destPath) === 'unload')
      const interceptScript = loadedScripts.find(script => script.getNavAction?.(destPath) === 'intercept')

      runNavigation(destPath, null, unloadHooks, interceptScript)
        .finally(() => {
          initScripts()
        })
    })

    // Hook up nav interceptors
    document.addEventListener('click', (ev) => {
      const link = (ev.target as HTMLElement)?.closest('a')
      const href = link?.getAttribute('href')

      if (!link || !href || href.match(/(https?:)?\/\//)) return

      if (ev.button !== 0) return
      if (ev.ctrlKey) return
      if (ev.metaKey) return

      // Skip if we've got a no-animate flag
      if (window.localStorage.getItem('tempest-noanimate') === "true" || window.matchMedia('(prefers-reduced-motion)').matches)
        return

      // Otherwise interrupt the click handling, it's time to do custom shit
      ev.preventDefault()

      const unloadHooks = loadedScripts.filter(script => script.getNavAction?.(link.pathname) === 'unload')
      const interceptScript = loadedScripts.find(script => script.getNavAction?.(link.pathname) === 'intercept')

      window.history.pushState(null, "", link.pathname)
      runNavigation(link.pathname, link, unloadHooks, interceptScript)
        .finally(() => {
          initScripts()
        })
    })

    await initScripts()
  })
}

async function runNavigation(path: string, link: HTMLAnchorElement | null, unloadHooks: NavScript[], interceptScript?: NavScript) {
  for (const script of unloadHooks) {
    await script.onBeforeUnload?.()
  }

  // Skip if we've got 

  await interceptScript?.onInterceptStart?.(path, link ?? undefined)

  let nextPage: string
  try {
    nextPage = await fetch(path).then(res => res.text())
  } catch (err) {
    console.error('Aborting clean transition due to network error')
    console.error(err)
    window.location.href = path
    return
  }

  const navFrame = initFrame()
  navFrame.contentDocument?.open()
  navFrame.contentDocument?.write(nextPage)
  navFrame.contentDocument?.close()
  const nextTitle = navFrame.contentDocument?.title || document.title
  document.title = nextTitle

  let result: Awaited<ReturnType<NonNullable<NavScript['onInterceptNav']>>> = { replaceDom: true }

  try {
    if (interceptScript?.onInterceptNav)
      result = await interceptScript?.onInterceptNav?.(path, navFrame)
    else
      console.info('No intercept script found, falling back to replacement')
  } catch (err) {
    console.error('Error when running intercept nav, falling back to replacement')
    console.error(err)
  }

  if (result?.replaceDom) {
    const navFrame = initFrame()
    navFrame.contentDocument?.open()
    navFrame.contentDocument?.write(nextPage)
    navFrame.contentDocument?.close()
    document.title = nextTitle

    if (navFrame.contentDocument) {
      const newBody = document.adoptNode(navFrame.contentDocument.body)
      document.body.replaceWith(newBody)
    } else {
      document.open()
      document.write(nextPage)
      document.close()
    }
  }

  await initScripts()
}

async function initScripts() {
  const layoutScripts = Array.from(document.querySelectorAll('script[type="tempest/layout"]'))
  const pageScripts = Array.from(document.querySelectorAll('script[type="tempest/page"]'))

  // Start loading scripts
  const scriptLoadPromises = await Promise.allSettled([...layoutScripts, ...pageScripts]
    .map(scriptElem => scriptElem.getAttribute('src') as string)
    .filter(scriptSrc => !!scriptSrc)
    .map(scriptSrc => import(scriptSrc) as Promise<NavScript>))

  // Sort out successfully loaded ones
  loadedScripts = scriptLoadPromises
    .map(result => result.status === "fulfilled" ? result.value : null)
    .filter(result => result !== null) as NavScript[]

  for (const script of loadedScripts) {
    if ('onLoad' in script)
      await script.onLoad?.()
  }
}

let navFrame: HTMLIFrameElement;
function initFrame(): HTMLIFrameElement {
  removeFrame()

  navFrame = document.createElement('iframe')
  navFrame.setAttribute('style', `
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    border: none;
    pointer-events: none;
  `.split('\n').map(s => s.trim()).join(''))

  document.body.appendChild(navFrame)
  return navFrame
}

function removeFrame() {
  if (navFrame) {
    try {
      document.body.removeChild(navFrame)
    } catch (err) {
    }
  }
}

